<script>
import { feature } from '@/utils/feature.helper'
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import { ddlHelper, sharedHelper } from '@/utils'
import { organizationService } from '@/helpers/services/organization.service'
import { tonePackService } from '@/helpers/services/tonePack.service'

/**
 * One brand, everything about it.
 *
 * ── Why one page and not four dialogs ────────────────────────────────────
 * The embed snippet, the backend credentials, what the bot says when it is
 * stuck, and what the month has cost were each heading for their own modal on
 * the listing. That is four places to look for "set this client up", and the
 * listing is a place for finding a brand, not for configuring one. Everything
 * that belongs to a brand lives here.
 *
 * ── What is NOT editable here ────────────────────────────────────────────
 * The name and the brand id. ai_bot owns both — renaming through this console
 * would re-point the mirror row and leave every conversation attached to a
 * brand that no longer exists under that name.
 */
export default {
  components: { Layout, PageHeader, MultiSelect },
  page() {
    return { title: this.brand ? this.brand.name : i18n.t('module.organization') }
  },

  /*
   * The same shape the user form uses: a red border and a message under the
   * field, not a disabled button that explains nothing.
   *
   * `$dirty` gates it, so the tab does not open complaining about a field
   * nobody has touched.
   */
  /** ai_bot paths that have a field on this screen. Everything else falls
   *  back to the alert. */
  FIELD_PATHS: ['persona.bot_name', 'persona.tone', 'persona.greeting', 'intent_mode'],

  data() {
    return {
      loading: true,
      error: '',
      brand: null,
      widget: null,
      webhook: null,
      webhookSecretVisible: false,
      copiedSecret: false,
      rotateAsk: false,
      rotating: false,
      usage: null,
      copied: false,
      copiedWebhook: false,
      /** Which of the money webhook URLs was just copied ('deposit' | 'withdraw' | null). */
      copiedWhich: null,

      form: {
        skills: [],
        /*
         * The three JK notifications. All default OFF — `bonusClaimed` is the
         * one that makes ai_bot poll JK's API on a timer, so it is not a
         * switch anything should flip on a brand's behalf.
         */
        jkNotifyDeposit: false,
        jkNotifyWithdraw: false,
        jkNotifyBonusClaimed: false,
        intentMode: 'llm',
        botName: '',
        tone: '',
        greeting: '',
        onNoAnswer: false,
        memoryEnabled: false,
        memoryFields: [],
        memoryCustom: '',
        /** Which industry's vocabulary the bot speaks. Its own setting. */
        vertical: '',
        /** How the bot treats the customer's tone — see the Tone section. */
        toneMode: 'live',
        toneAskWhy: true,
        toneRepeatHandoff: true,
      },
      /** Inbox row colour per handover reason — the brand's own picks. */
      reasonColors: {},
      reasonColorsSaving: false,
      /** Proposal tab on this brand's conversations (inbox_settings.proposal). */
      proposalTab: false,
      /** The industry packs that exist, for the Industry picker. Typing the
       *  name by hand was a trap: a misspelling saves fine and the bot then
       *  reads this trade's slang as plain words, with nothing on screen
       *  disagreeing. You can only pick one that exists. */
      packs: [],
      /** Reasons ai_bot produces today; a brand's own yaml reasons appear once used. */
      knownReasons: ['deposit', 'withdrawal', 'complaint', 'price_objection', 'topic_only', 'faq_pass', 'no_answer', 'classifier_handoff', 'tenant_skill_disabled', 'explicit_request', 'tone_hostile', 'tone_praise', 'tone_lost', 'tone_unsure'],
      /** Fields the extractor has a definition for; a brand may add its own. */
      memoryKnownFields: ['name', 'language', 'last_topic', 'open_refs', 'company', 'role', 'location', 'pax', 'preferences', 'interests'],
      /** {ready, schema} from ai_bot — whether the bot can take a booking. */
      apptForm: null,
      apptFormCreating: false,
      /** Editable rows of the booking form; see apptRowsFrom. */
      apptRows: [],
      apptFormSaving: false,
      apptRowErrors: {},
      /** Needed by the appointment skill to file and book; shown locked. */
      apptLocked: ['name', 'phone', 'preferred_date', 'preferred_time'],
      apptTypes: ['text', 'number', 'quantity', 'enum', 'ic_my', 'ic_foreign', 'phone_my', 'email', 'date'],
      skillDdl: ddlHelper.getBotSkills(),
      copyLangs: ['en', 'ms', 'zh', 'rojak'],


      copyRows: [],
      copySaving: false,
      translating: '',

      /** One row per backend provider, from ai_bot's registry — see the Backend tab. */
      creds: [],

      saving: false,
      /** Server-side rejections, keyed by the path ai_bot named. */
      fieldErrors: {},
    }
  },
  computed: {
    /** The packs, plus whatever this brand already names so a value set
     *  before the pack existed is never silently dropped on save. */
    verticalDdl() {
      const names = this.packs.map((p) => p.name)
      const current = (this.form.vertical || '').trim()
      if (current && !names.includes(current)) names.unshift(current)
      return names.map((n) => ({ value: n, text: n }))
    },
    toneModeDdl() {
      return [
        { value: 'off', text: this.$t('organization.toneMode_off') },
        { value: 'shadow', text: this.$t('organization.toneMode_shadow') },
        { value: 'live', text: this.$t('organization.toneMode_live') },
      ]
    },
    intentModeDdl() {
      return [
        { value: 'llm', text: this.$t('organization.intentMode_llm') },
        { value: 'rag_only', text: this.$t('organization.intentMode_rag_only') },
        { value: 'receptionist', text: this.$t('organization.intentMode_receptionist') },
      ]
    },




    /** What ai_bot OBSERVES about this brand, as opposed to what is configured. */
    runtime() {
      return (this.brand && this.brand.bot && this.brand.bot._runtime) || {}
    },

    /** The site is running an embed older than the one the snippet installs. */
    widgetStale() {
      const r = this.runtime
      return Boolean(r.widget && r.widget_version_current && r.widget.version !== r.widget_version_current)
    },

    tenantId() {
      return Number(this.$route.params.tenantId)
    },
    breadcrumbItems() {
      return [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.organization'), href: '/organizations' },
        { text: this.brand ? this.brand.name : '…', active: true },
      ]
    },
    /** How much of the month's allowance is gone. Capped at 100 so the bar does
     *  not run off the card — the number next to it carries the overage. */
    usedPct() {
      if (!this.usage || !this.usage.included_turns) return 0
      return Math.min(100, Math.round((this.usage.chargeable_turns / this.usage.included_turns) * 100))
    },
    overBudget() {
      return Boolean(this.usage && this.usage.overage_turns > 0)
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = ''
      try {
        const [detail, creds, usage, copy] = await Promise.all([
          organizationService.getOrganization(this.tenantId),
          organizationService.getAllCredentials(this.tenantId).catch(() => null),
          // Usage scans a month of telemetry, so a failure here must not take
          // the whole page with it.
          organizationService.getUsage(this.tenantId).catch(() => null),
          organizationService.getCopy(this.tenantId).catch(() => null),
        ])

        const d = (detail && detail.data) || {}
        this.brand = d
        this.widget = d.widget || null
        this.webhook = d.webhook || null
        // The secret comes from its own call: it is platform-staff only and
        // read in clear, so it is not part of the detail payload.
        organizationService.getJkWebhook(this.tenantId)
          .then((r) => { if (r && r.data) this.webhook = { ...(this.webhook || {}), ...r.data } })
          .catch(() => {})
        this.usage = (usage && usage.data) || null
        this.copyRows = ((copy && copy.data) || []).map((r) => ({
          ...r,
          text: this.pickLang(r.brand || {}),
          platformText: this.pickLang(r.platform || {}),
        }))

        const bot = d.bot || {}
        this.form.skills = Object.keys(bot.skills || {}).filter((k) => bot.skills[k] && bot.skills[k].enabled)

        // Absent means off, which is why each is compared to true rather than
        // read for truthiness — a brand that has never seen this section has
        // no `notify` key at all.
        const jkNotify = ((bot.skills || {}).jk || {}).notify || {}
        this.form.jkNotifyDeposit = jkNotify.deposit === true
        this.form.jkNotifyWithdraw = jkNotify.withdraw === true
        this.form.jkNotifyBonusClaimed = jkNotify.bonus_claimed === true

        // The EFFECTIVE mode, not the stored field. The field is null for
        // every brand created here, and showing a blank dropdown for a brand
        // that is demonstrably running something is how today happened.
        this.form.intentMode = this.runtime.intent_mode_effective || bot.intent_mode || 'llm'

        const persona = bot.persona || {}
        this.form.botName = persona.bot_name || ''
        this.form.tone = persona.tone || ''
        // Show the one the operator is most likely to read: their console
        // language, then English, then whatever is there. All four are
        // stored; this picks which to put in the single box.
        this.form.greeting = this.pickLang(persona.greeting || {})

        const handoff = bot.handoff || {}
        this.form.onNoAnswer = Boolean(handoff.on_no_answer)

        this.reasonColors = { ...(((d.inbox_settings || {}).reason_colors) || {}) }
        this.proposalTab = Boolean(((d.inbox_settings || {}).proposal))

        const tone = bot.tone || {}
        // On unless the brand turned it off — the same default ai_bot applies.
        this.form.toneMode = tone.mode || 'live'
        this.form.vertical = bot.vertical || tone.pack || ''
        this.form.toneAskWhy = tone.ask_why !== false
        this.form.toneRepeatHandoff = tone.handoff_on_repeat_request !== false

        const memory = bot.memory || {}
        this.form.memoryEnabled = Boolean(memory.enabled)
        const mf = Array.isArray(memory.fields) && memory.fields.length ? memory.fields : ['name', 'language', 'last_topic', 'open_refs']
        this.form.memoryFields = mf.filter((f) => this.memoryKnownFields.includes(f))
        this.form.memoryCustom = mf.filter((f) => !this.memoryKnownFields.includes(f)).join(', ')


        await this.loadApptForm()

        // Best-effort: the picker falling back to whatever is already set
        // beats the whole page failing because one list could not be fetched.
        try {
          const packs = await tonePackService.list()
          this.packs = packs.data || []
        } catch (e) {
          this.packs = []
        }


        // `secret` stays empty on every row: what comes back is a hint, and
        // putting a hint in the field would post it back as if it were the token.
        this.creds = ((creds && creds.data) || []).map((r) => ({ ...r, secret: '', saving: false, error: '' }))
      } catch (e) {
        this.error = this.readApiError(e)
      } finally {
        this.loading = false
      }
    },

    /** "3 minutes ago" beats a timestamp for a liveness check. */
    formatSeen(iso) {
      if (!iso) return ''
      const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
      if (secs < 90) return this.$t('organization.seenJustNow')
      const mins = Math.round(secs / 60)
      if (mins < 90) return this.$t('organization.seenMinutes', [mins])
      const hours = Math.round(mins / 60)
      if (hours < 48) return this.$t('organization.seenHours', [hours])
      return this.$t('organization.seenDays', [Math.round(hours / 24)])
    },


    /** The value an operator should see in a single box, out of four stored. */
    pickLang(byLang) {
      const order = [this.$i18n.locale, 'en', 'ms', 'zh', 'rojak']
      for (const l of order) {
        if (byLang[l]) return byLang[l]
      }
      return ''
    },

    /**
     * One line in, four out.
     *
     * Translated at SAVE time, never at reply time: what lands in the config
     * is four fixed strings, so a customer never waits on a model and the
     * wording cannot drift between turns. That property is the whole reason
     * this copy is configuration rather than something generated.
     *
     * The source language is not asked for — the bot sniffs it. Making an
     * operator declare which language they just typed is a question with an
     * obvious answer that only they can get wrong.
     */
    async translateToAll(text) {
      const langs = ['en', 'ms', 'zh', 'rojak']
      const res = await organizationService.translateCopy(this.tenantId, text, 'auto', langs)
      const got = (res && res.data) || {}
      const out = {}
      langs.forEach((l) => {
        if (got[l]) out[l] = got[l]
      })
      // The sniffed source comes back; its own text is the original, not a
      // round trip through the model.
      if (res && res.source) out[res.source] = text
      return Object.keys(out).length ? out : { en: text }
    },

    async persistCopy() {
      this.copySaving = true
      this.error = ''
      try {
        /*
         * One field can stand for more than one copy key.
         *
         * "The knowledge base has no answer" and "the message was not
         * understood" are different inside the bot and the same sentence to
         * whoever writes it, so the editor shows one field and the save fans
         * it out. Shown as two, a brand customises the common one, leaves the
         * other at the platform default, and the bot switches voices
         * depending on which way it failed.
         */
        const payload = {}
        for (const r of this.copyRows) {
          const text = (r.text || '').trim()
          // Blank clears the override back to the platform wording, and must
          // not be translated into four copies of nothing.
          const byLang = text ? await this.translateToAll(text) : {}
          const keys = r.keys && r.keys.length ? r.keys : [r.key]
          keys.forEach((k) => {
            payload[k] = byLang
          })
        }
        await organizationService.saveCopy(this.tenantId, payload)
        // Re-read so the box shows what was actually stored rather than what
        // was typed — if the sniff or a translation came back empty, that is
        // worth seeing now and not on the next page load.
        const fresh = await organizationService.getCopy(this.tenantId).catch(() => null)
        if (fresh && fresh.data) {
          this.copyRows = fresh.data.map((r) => ({
            ...r,
            text: this.pickLang(r.brand || {}),
            platformText: this.pickLang(r.platform || {}),
          }))
        }
        sharedHelper.alertSuccessMessage(i18n.t('organization.jkSaved'))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readApiError(e))
      } finally {
        this.copySaving = false
      }
    },



    /**
     * Turn ai_bot's rejection into per-field messages.
     *
     * It answers with a list of `{path, message}` — `persona.tone: Expected
     * string, received null`. A red bar at the top of the page saying the
     * config is invalid is the state the create-user form has never been in:
     * there, the field turns red and says what it needs, and that is the whole
     * difference between "something is wrong" and "fix this box".
     *
     * Anything whose path does not match a field on screen stays in the bar,
     * because an error nobody can see is worse than one in the wrong place.
     */
    applyFieldErrors(e) {
      const body = (e && e.response && e.response.data) || {}
      const issues = Array.isArray(body.issues) ? body.issues : []
      const mapped = {}
      const unmapped = []
      for (const i of issues) {
        const path = (i && i.path) || ''
        if (this.FIELD_PATHS.indexOf(path) !== -1) mapped[path] = i.message
        else unmapped.push(path ? `${path}: ${i.message}` : i.message)
      }
      this.fieldErrors = mapped
      /*
       * A dialog only for what the screen cannot show.
       *
       * Issues that landed on a field are already visible there, in red, next
       * to the box that has to change — a dialog repeating them would make
       * somebody dismiss it to go and read the thing it just said. What has no
       * field on screen has nowhere else to go, so that gets the dialog.
       */
      if (Object.keys(mapped).length === 0 || unmapped.length) {
        sharedHelper.alertErrorMessageWithoutCode(
          unmapped.length ? `${this.readApiError(e)} — ${unmapped.join('; ')}` : this.readApiError(e),
        )
      }
    },

    async saveSettings() {
      this.saving = true
      this.error = ''
      this.fieldErrors = {}
      try {
        /*
         * Every skill this form OFFERS, with its true/false — not just the
         * ticked ones.
         *
         * Posting only the ticked names made unticking a no-op (the server
         * cannot tell "off" from "not mentioned"), and it used to be worse
         * than a no-op: ai_bot replaced the whole skills map, so saving any
         * toggle here switched off `block` and `unknown`, which this form
         * deliberately does not show, and wiped rag's threshold and kb_docs.
         * Skills the form does not offer stay untouched by being left out.
         */
        const greetingText = (this.form.greeting || '').trim()
        const greeting = greetingText ? await this.translateToAll(greetingText) : {}

        const skills = {}
        this.skillDdl.forEach((o) => {
          skills[o.value] = this.form.skills.indexOf(o.value) !== -1
        })


        await organizationService.updateSettings(this.tenantId, {
          skills,
          intent_mode: this.form.intentMode,
          /*
           * Empty means ABSENT, not null.
           *
           * ai_bot's schema takes a string or nothing; `null` is neither, so
           * clearing Tone and pressing Submit came back as "the resulting
           * config is not valid" with no clue which field. And absent is what
           * clearing should mean anyway — the PATCH replaces `persona`
           * wholesale, so a key left out is a key removed.
           */
          persona: {
            ...(this.form.botName ? { bot_name: this.form.botName } : {}),
            ...(this.form.tone ? { tone: this.form.tone } : {}),
            // One line typed, four stored — see translateToAll.
            ...(Object.keys(greeting).length ? { greeting } : {}),
          },
          // `handoff.target` and the always-handoff list are not edited
          // here any more, and are deliberately NOT sent: ai_bot merges what
          // it is given, so leaving them out keeps whatever the brand has.
          // Sent whenever the brand has jk on, and sent in full: ai_bot merges
          // per key inside a skill, so an omitted switch would keep its old
          // value rather than turn off. Switching a notification off has to be
          // a statement, same as switching a skill off.
          ...(this.form.skills.includes('jk')
            ? {
                jk_notify: {
                  deposit: this.form.jkNotifyDeposit,
                  withdraw: this.form.jkNotifyWithdraw,
                  bonus_claimed: this.form.jkNotifyBonusClaimed,
                },
              }
            : {}),
          handoff: { on_no_answer: this.form.onNoAnswer },
          memory: { enabled: this.form.memoryEnabled, fields: this.memoryFieldList() },
          vertical: (this.form.vertical || '').trim(),
          tone: {
            mode: this.form.toneMode,
            ask_why: this.form.toneAskWhy,
            handoff_on_repeat_request: this.form.toneRepeatHandoff,
          },
        })
        sharedHelper.alertSuccessMessage(i18n.t('organization.jkSaved'))
      } catch (e) {
        this.applyFieldErrors(e)
      } finally {
        this.saving = false
      }
    },

    async createApptForm() {
      this.apptFormCreating = true
      this.error = ''
      try {
        await organizationService.createAppointmentForm(this.tenantId)
        await this.loadApptForm()
        sharedHelper.alertSuccessMessage(i18n.t('organization.apptFormCreated'))
      } catch (e) {
        this.applyFieldErrors(e)
      } finally {
        this.apptFormCreating = false
      }
    },

    async loadApptForm() {
      // A failure here must not take the page down: the editor just shows
      // "no booking form" and the create button.
      const res = await organizationService.getAppointmentForm(this.tenantId).catch(() => null)
      this.apptForm = (res && res.data) || null
      this.apptRows = this.apptRowsFrom(this.apptForm && this.apptForm.schema)
      this.apptRowErrors = {}
    },

    /*
     * One box per label, not four.

     * The stored form carries every label in en / ms / zh / rojak. The editor
     * shows the operator's language and keeps the other three behind the row;
     * a label they actually changed is re-translated on save, one they did not
     * touch keeps all four as stored.
     */
    apptRowsFrom(schema) {
      const fields = (schema && schema.fields) || []
      return fields.map((f) => ({
        name: f.name,
        emoji: f.emoji || '',
        type: f.type || 'text',
        required: Boolean(f.required),
        label: this.pickLang(f.label || {}),
        labelAll: { ...(f.label || {}) },
        labelOriginal: this.pickLang(f.label || {}),
        hint: this.pickLang(f.hint || {}),
        hintAll: { ...(f.hint || {}) },
        hintOriginal: this.pickLang(f.hint || {}),
        options: (f.options || []).join(', '),
        locked: this.apptLocked.indexOf(f.name) !== -1,
      }))
    },

    apptAddRow() {
      this.apptRows.push({
        name: '', emoji: '', type: 'text', required: true,
        label: '', labelAll: {}, labelOriginal: null,
        hint: '', hintAll: {}, hintOriginal: null,
        options: '', locked: false,
      })
    },

    apptRemoveRow(i) {
      if (this.apptRows[i] && this.apptRows[i].locked) return
      this.apptRows.splice(i, 1)
    },

    apptMoveRow(i, dir) {
      const j = i + dir
      if (j < 0 || j >= this.apptRows.length) return
      const rows = this.apptRows.slice()
      const [row] = rows.splice(i, 1)
      rows.splice(j, 0, row)
      this.apptRows = rows
    },

    async saveApptForm() {
      this.apptFormSaving = true
      this.error = ''
      this.apptRowErrors = {}
      try {
        const fields = []
        for (const r of this.apptRows) {
          const label = (r.label || '').trim()
          const hint = (r.hint || '').trim()
          const labelAll = label && label !== r.labelOriginal ? await this.translateToAll(label) : r.labelAll
          const hintAll = hint ? (hint !== r.hintOriginal ? await this.translateToAll(hint) : r.hintAll) : {}
          fields.push({
            name: (r.name || '').trim().toLowerCase(),
            emoji: r.emoji || null,
            type: r.type,
            required: r.locked ? true : Boolean(r.required),
            label: labelAll,
            hint: Object.keys(hintAll).length ? hintAll : null,
            options: r.type === 'enum'
              ? (r.options || '').split(',').map((o) => o.trim()).filter(Boolean)
              : null,
          })
        }
        const res = await organizationService.saveAppointmentForm(this.tenantId, fields)
        this.apptForm = (res && res.data) || this.apptForm
        this.apptRows = this.apptRowsFrom(this.apptForm && this.apptForm.schema)
        sharedHelper.alertSuccessMessage(i18n.t('organization.apptFormSaved'))
      } catch (e) {
        // ai_bot names the row: `fields.2.name`. Put the message on that row.
        const issues = (e && e.response && e.response.data && e.response.data.issues) || []
        const byRow = {}
        issues.forEach((it) => {
          const m = /^fields\.(\d+)\./.exec(it.path || '')
          const key = m ? m[1] : '_'
          byRow[key] = (byRow[key] ? byRow[key] + ' ' : '') + it.message
        })
        this.apptRowErrors = byRow
        if (!issues.length) this.applyFieldErrors(e)
        else this.error = issues.map((it) => it.message).join('; ')
      } finally {
        this.apptFormSaving = false
      }
    },

    /** Providers behind a feature switch disappear with it (One Sales ↔ one_sales_crm). */
    visibleCreds() {
      return this.creds.filter((r) => r.provider !== 'one_sales' || feature('one_sales_crm'))
    },
    /** Every reason to show a picker for: the known set plus any already coloured. */
    reasonRows() {
      return [...new Set([...this.knownReasons, ...Object.keys(this.reasonColors)])]
    },
    reasonLabel(r) {
      const key = 'inbox.reason_' + r
      return this.$te(key) ? this.$t(key) : String(r).replace(/_/g, ' ')
    },
    setReasonColor(r, hex) {
      this.$set(this.reasonColors, r, hex)
    },
    clearReasonColor(r) {
      this.$delete(this.reasonColors, r)
    },
    async saveReasonColors() {
      this.reasonColorsSaving = true
      try {
        const res = await organizationService.updateInboxSettings(this.tenantId, { reason_colors: this.reasonColors, proposal: this.proposalTab })
        const d = (res && res.data) || {}
        this.reasonColors = { ...(((d.inbox_settings || {}).reason_colors) || {}) }
        this.proposalTab = Boolean(((d.inbox_settings || {}).proposal))
        sharedHelper.alertSuccessMessage(i18n.t('organization.jkSaved'))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readApiError(e))
      } finally {
        this.reasonColorsSaving = false
      }
    },

    /** Known fields ticked plus whatever the brand typed, as snake_case keys. */
    memoryFieldList() {
      const custom = (this.form.memoryCustom || '')
        .split(/[,\n]/)
        .map((x) => x.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, ''))
        .filter(Boolean)
      return [...new Set([...this.form.memoryFields, ...custom])].slice(0, 12)
    },

    /** One provider at a time: each card has its own Save. */
    async saveCred(row) {
      row.error = ''
      if (!row.endpoint) {
        row.error = i18n.t('message.requiredX', [row.fields.endpoint])
        return
      }
      row.saving = true
      try {
        const payload = { endpoint: row.endpoint, public_id: row.public_id }
        // Only when they typed a new one. Empty = keep the stored token.
        if (row.secret) payload.secret = row.secret
        const res = await organizationService.saveCredentials(this.tenantId, payload, row.provider)
        const d = res.data || {}
        row.endpoint = d.endpoint || ''
        row.public_id = d.public_id || ''
        row.secret = ''
        row.has_secret = Boolean(d.has_secret)
        row.secret_hint = d.secret_hint || ''
        sharedHelper.alertSuccessMessage(i18n.t('organization.jkSaved'))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readApiError(e))
      } finally {
        row.saving = false
      }
    },


    async copySecret() {
      try {
        await navigator.clipboard.writeText(this.webhook.secret)
        this.copiedSecret = true
      } catch (e) {
        // On screen when revealed; selectable.
      }
    },

    async rotateSecret() {
      if (this.rotating) return
      this.rotating = true
      try {
        const r = await organizationService.rotateJkWebhook(this.tenantId)
        if (r && r.data) this.webhook = { ...(this.webhook || {}), ...r.data }
        this.webhookSecretVisible = true
        this.copiedSecret = false
      } catch (e) {
        this.$bvToast && this.$bvToast.toast(this.readApiError(e) || 'Could not rotate the secret.', { variant: 'danger', solid: true })
      } finally {
        this.rotating = false
        this.rotateAsk = false
      }
    },

    /**

     * The deposit / withdrawal webhook URL with the brand's secret as `?s=`.

     * JK's webhook config box could not be made to send a header for these

     * events, and ai_bot accepts the secret in the query string for exactly

     * that reason. `masked` is for display: the secret shows only once the

     * operator has clicked to reveal it, the same rule as the secret box below.

     */

    signedWebhookUrl(url, masked = false) {

      if (!url || !this.webhook.has_secret || !this.webhook.secret) return url

      const secret = masked && !this.webhookSecretVisible ? '••••••••••••••••' : this.webhook.secret

      return url + (url.includes('?') ? '&' : '?') + 's=' + secret

    },

    async copyText(text, which) {
      try {
        await navigator.clipboard.writeText(text)
        this.copiedWhich = which
        setTimeout(() => { if (this.copiedWhich === which) this.copiedWhich = null }, 1500)
      } catch (e) { /* clipboard blocked — the text is on screen to select */ }
    },

    async copyWebhook() {
      try {
        await navigator.clipboard.writeText(this.webhook.url)
        this.copiedWebhook = true
      } catch (e) {
        // Same as copyEmbed: the URL is on screen and selectable.
      }
    },

    async copyEmbed() {
      try {
        await navigator.clipboard.writeText(this.widget.snippet)
        this.copied = true
      } catch (e) {
        // Blocked outside a secure context. The snippet is on screen and
        // selectable, so say nothing rather than claim a failure they can work
        // around by selecting it.
      }
    },

    /** ai_bot's own sentence where there is one — "endpoint must not carry a
     *  query string" is the whole value of the check and has to reach whoever
     *  typed the URL. */
    readApiError(e) {
      const d = e && e.response && e.response.data
      if (d && typeof d.error === 'string') return d.error
      if (d && d.error && typeof d.error === 'object') return Object.values(d.error).flat().join(' ')
      return i18n.t('message.error')
    },

    back() {
      this.$router.push('/organizations')
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="brand ? brand.name : '…'" :items="breadcrumbItems" />

    <div v-if="loading" class="card"><div class="card-body text-muted">{{ $t('label.loading') }}</div></div>

    <template v-else>
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <!--
        Five tabs, five jobs.

        All of it at once was a page somebody scrolls past: reading the month,
        installing the widget, setting the bot up, writing what it says and
        wiring the backend are five different reasons to be here, and a person
        doing one of them has no use for the other four on screen.

        Each tab keeps its own Submit. They go to different endpoints — bot
        config, copy, credentials — so one button for all of them would be a
        lie about what it does, and a failure in one would leave somebody
        guessing which part saved.
      -->
      <b-card no-body>
        <b-tabs card>
          <b-tab :title="$t('organization.tabUsage')" active>
            <!-- ── Usage ──────────────────────────────────────────────────── -->
              <div class="card">
                <div class="card-header bg-white py-2 d-flex align-items-center">
                  <h6 class="mb-0">{{ $t('organization.usageTitle') }}</h6>
                  <span v-if="usage" class="small text-muted ml-2">{{ usage.period }}</span>
                </div>
                <div class="card-body">
                  <div v-if="!usage" class="small text-muted">{{ $t('organization.usageUnavailable') }}</div>
                  <template v-else>
                    <div class="d-flex align-items-baseline mb-1">
                      <span class="h4 mb-0" :class="overBudget ? 'text-danger' : ''">
                        {{ usage.chargeable_turns }}
                      </span>
                      <span class="text-muted ml-2">/ {{ usage.included_turns }} {{ $t('organization.turns') }}</span>
                      <b-badge v-if="overBudget" variant="danger" class="ml-2">
                        +{{ usage.overage_turns }} {{ $t('organization.overage') }}
                      </b-badge>
                    </div>
                    <b-progress :max="100" height="6px" class="mb-3">
                      <b-progress-bar :value="usedPct" :variant="overBudget ? 'danger' : 'primary'"></b-progress-bar>
                    </b-progress>

                    <!-- The split, because "why is it higher than my chat volume"
                         is the first question a bill gets. -->
                    <table class="table table-sm small mb-0">
                      <tbody>
                        <tr>
                          <td class="text-muted">{{ $t('organization.chatTurns') }}</td>
                          <td class="text-right">{{ usage.chat_turns }}</td>
                        </tr>
                        <tr>
                          <td class="text-muted">{{ $t('organization.consoleTurns') }}</td>
                          <td class="text-right">{{ usage.console_turns }}</td>
                        </tr>
                        <tr>
                          <td class="text-muted">{{ $t('organization.conversations') }}</td>
                          <td class="text-right">{{ usage.conversations }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <small class="form-text text-muted">{{ $t('organization.consoleTurnsHint') }}</small>
                    <!-- Said here because somebody looking at an overage will ask
                         where the 3,000 came from, and it is not on this page. -->
                    <small class="form-text text-muted">{{ $t('organization.includedFromConfig') }}</small>
                  </template>
                </div>
              </div>
          </b-tab>

          <b-tab :title="$t('organization.tabEmbed')">
            <!-- ── Embed ──────────────────────────────────────────────────── -->
              <div class="card">
                <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('organization.embedCode') }}</h6></div>
                <div class="card-body">
                  <div v-if="!widget" class="small text-muted">{{ $t('organization.embedUnavailable') }}</div>
                  <template v-else>
                    <p class="small text-muted">{{ $t('organization.embedHint', [brand.name]) }}</p>
                    <pre class="embed-code">{{ widget.snippet }}</pre>
                    <b-button variant="primary" size="sm" @click="copyEmbed">
                      <i class="mdi mdi-content-copy mr-1"></i>{{ copied ? $t('organization.copied') : $t('label.copy') }}
                    </b-button>
                    <small class="form-text text-muted mt-2">{{ $t('organization.embedWhereNot') }}</small>
                    <small class="form-text text-muted mt-1">{{ $t('organization.embedTokenNote') }}</small>

                    <!--
                      Is this snippet actually installed?

                      The snippet above has always been correct; what nobody could
                      see was whether the brand's site is running it. On 2026-10-06
                      agent replies were not reaching players because the site had
                      an old copy of the embed inlined in the page, and that copy
                      does not poll for them. Everything else checked out, so the
                      search went everywhere except the one place that was wrong.
                      The widget now reports its version on every poll.
                    -->
                    <hr class="my-3" />
                    <div class="small">
                      <div v-if="!runtime.widget" class="text-danger">
                        <i class="mdi mdi-alert-circle-outline mr-1"></i>
                        {{ $t('organization.widgetNeverSeen') }}
                      </div>
                      <template v-else>
                        <div :class="widgetStale ? 'text-warning' : 'text-success'">
                          <i class="mdi mr-1" :class="widgetStale ? 'mdi-alert-outline' : 'mdi-check-circle-outline'"></i>
                          {{ $t('organization.widgetSeen', [runtime.widget.version, formatSeen(runtime.widget.last_seen_at)]) }}
                        </div>
                        <div v-if="widgetStale" class="text-muted mt-1">
                          {{ $t('organization.widgetStale', [runtime.widget_version_current]) }}
                        </div>
                        <div v-if="runtime.widget.page_origin" class="text-muted mt-1 text-break">
                          {{ runtime.widget.page_origin }}
                        </div>
                      </template>
                    </div>
                  </template>
                </div>
              </div>

          
              <!-- ── Webhook (JK live chat) ─────────────────────────────────── -->
              <div class="card mt-3">
                <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('organization.webhookTitle') }}</h6></div>
                <div class="card-body">
                  <div v-if="!webhook" class="small text-muted">{{ $t('organization.webhookUnavailable') }}</div>
                  <template v-else>
                    <p class="small text-muted">{{ $t('organization.webhookHint', [brand.name]) }}</p>
                    <pre class="embed-code">{{ webhook.url }}</pre>
                    <b-button variant="primary" size="sm" @click="copyWebhook">
                      <i class="mdi mdi-content-copy mr-1"></i>{{ copiedWebhook ? $t('organization.copied') : $t('label.copy') }}
                    </b-button>
                    <!-- Deposit / withdrawal events: JK's admin takes a list of
                         webhook URLs per event; these two are appended after
                         the operator's own. Same secret header as live chat. -->
                    <template v-if="webhook.deposit_url || webhook.withdraw_url">
                      <hr />
                      <h6 class="mb-1">{{ $t('organization.moneyWebhookTitle') }}</h6>
                      <p class="small text-muted">{{ $t('organization.moneyWebhookHint') }}</p>
                      <!-- The secret rides IN the URL (`?s=`). JK's webhook box for
                           deposits sent no header at all on staging (2026-11-02: four
                           POSTs, every one 401), so the URL to paste is the one that
                           already carries it. Shown masked until the secret is revealed. -->
                      <p v-if="webhook.has_secret" class="small text-muted">{{ $t('organization.moneyWebhookSecretHint') }}</p>
                      <div v-if="webhook.deposit_url" class="mb-2">
                        <div class="small font-weight-bold">{{ $t('organization.depositWebhook') }}</div>
                        <pre class="embed-code">{{ signedWebhookUrl(webhook.deposit_url, true) }}</pre>
                        <b-button variant="outline-primary" size="sm" @click="copyText(signedWebhookUrl(webhook.deposit_url), 'deposit')">
                          <i class="mdi mdi-content-copy mr-1"></i>{{ copiedWhich === 'deposit' ? $t('organization.copied') : $t('label.copy') }}
                        </b-button>
                      </div>
                      <div v-if="webhook.withdraw_url" class="mb-2">
                        <div class="small font-weight-bold">{{ $t('organization.withdrawWebhook') }}</div>
                        <pre class="embed-code">{{ signedWebhookUrl(webhook.withdraw_url, true) }}</pre>
                        <b-button variant="outline-primary" size="sm" @click="copyText(signedWebhookUrl(webhook.withdraw_url), 'withdraw')">
                          <i class="mdi mdi-content-copy mr-1"></i>{{ copiedWhich === 'withdraw' ? $t('organization.copied') : $t('label.copy') }}
                        </b-button>
                      </div>
                    </template>
                    <hr />
                    <h6 class="mb-1">{{ $t('organization.webhookSecret') }}</h6>
                    <p class="small text-muted">{{ $t('organization.webhookSecretHint', [webhook.header || webhook.secret_header || 'x-jk-secret']) }}</p>
                    <template v-if="webhook.has_secret">
                      <pre class="embed-code">{{ webhookSecretVisible ? webhook.secret : '••••••••••••••••••••••••••••••••••••••••••••••••' }}</pre>
                      <b-button variant="outline-secondary" size="sm" class="mr-2" @click="webhookSecretVisible = !webhookSecretVisible">
                        <i class="mdi mr-1" :class="webhookSecretVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></i>{{ webhookSecretVisible ? $t('organization.hideSecret') : $t('organization.showSecret') }}
                      </b-button>
                      <b-button variant="primary" size="sm" class="mr-2" @click="copySecret">
                        <i class="mdi mdi-content-copy mr-1"></i>{{ copiedSecret ? $t('organization.copied') : $t('organization.copySecret') }}
                      </b-button>
                    </template>
                    <div v-else class="small text-muted mb-2">{{ $t('organization.secretNone') }}</div>
                    <template v-if="!rotateAsk">
                      <b-button :variant="webhook.has_secret ? 'outline-danger' : 'primary'" size="sm" @click="rotateAsk = true">
                        <i class="mdi mdi-refresh mr-1"></i>{{ webhook.has_secret ? $t('organization.rotateSecret') : $t('organization.generateSecret') }}
                      </b-button>
                    </template>
                    <div v-else class="d-flex align-items-center flex-wrap">
                      <span class="small text-danger mr-2">{{ webhook.has_secret ? $t('organization.rotateConfirm') : $t('organization.generateConfirm') }}</span>
                      <b-button variant="danger" size="sm" class="mr-2" :disabled="rotating" @click="rotateSecret">{{ $t('organization.rotateYes') }}</b-button>
                      <b-button variant="light" size="sm" :disabled="rotating" @click="rotateAsk = false">{{ $t('organization.rotateNo') }}</b-button>
                    </div>
                  </template>
                </div>
              </div>
          </b-tab>

          <b-tab :title="$t('organization.tabBot')">
            <!-- ── Bot settings ─────────────────────────────────────────────── -->
            <div class="card">
            <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('organization.botSettings') }}</h6></div>
            <div class="card-body">
              <b-row>
                <b-col cols="12" lg="6">
                  <!--
                    How a turn picks its skill.

                    Operator-only until today, which is how a brand ended up
                    running the two-stage classifier while the config file said
                    rag_only — a difference that swaps the entire routing path and
                    could only be inferred from a MISSING field in a log line.
                  -->
                  <b-form-group :label="$t('organization.intentMode')">
                    <MultiSelect
                      v-model="form.intentMode"
                      :options="intentModeDdl"
                      :multiple="false"
                      :allow-empty="false"
                      :invalid="Boolean(fieldErrors['intent_mode'])"
                    />
                    <small v-if="fieldErrors['intent_mode']" class="text-danger d-block mt-1">
                      {{ fieldErrors['intent_mode'] }}
                    </small>
                    <small class="form-text text-muted">{{ $t('organization.intentModeHint_' + form.intentMode) }}</small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6">
                  <b-form-group :label="$t('organization.botSkills')">
                    <MultiSelect v-model="form.skills" :options="skillDdl" :hint="$t('organization.skillsHint')" />
                  </b-form-group>
                </b-col>
              </b-row>

              <hr />

              <b-row>
                <b-col cols="12" lg="4">
                  <b-form-group :label="$t('organization.botName')">
                    <b-form-input
                      v-model.trim="form.botName"
                      :state="fieldErrors['persona.bot_name'] ? false : null"
                    />
                    <b-form-invalid-feedback>{{ fieldErrors['persona.bot_name'] }}</b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="8">
                  <b-form-group :label="$t('organization.tone')">
                    <b-form-input
                      v-model.trim="form.tone"
                      :placeholder="$t('organization.tonePlaceholder')"
                      :state="fieldErrors['persona.tone'] ? false : null"
                    />
                    <b-form-invalid-feedback>{{ fieldErrors['persona.tone'] }}</b-form-invalid-feedback>
                    <small class="form-text text-muted">{{ $t('organization.toneHint') }}</small>
                  </b-form-group>
                </b-col>
              </b-row>

              <!--
                One message, not four.

                Asking for the same sentence in four languages is four times the
                work and three chances to leave one blank — and a blank one is the
                brand's voice dropping out for whoever speaks that language. The
                operator types it once, in whatever language they think in, and
                the other three are translated when they save. What gets STORED is
                still four fixed strings, so a customer never waits on a
                translation and the wording cannot drift between turns.
              -->
              <b-form-group :label="$t('organization.greeting')">
                <b-form-input
                  v-model="form.greeting"
                  :placeholder="$t('organization.greetingDefault')"
                  :state="fieldErrors['persona.greeting'] ? false : null"
                />
                <b-form-invalid-feedback>{{ fieldErrors['persona.greeting'] }}</b-form-invalid-feedback>
                <small class="form-text text-muted">{{ $t('organization.greetingHint') }}</small>
              </b-form-group>

              <hr />

              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('organization.onNoAnswer')">
                    <b-form-checkbox v-model="form.onNoAnswer" switch>
                      {{ $t('organization.onNoAnswerLabel') }}
                    </b-form-checkbox>
                    <small class="form-text text-muted">{{ $t('organization.onNoAnswerHint') }}</small>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr />

              <!-- ── Memory ──────────────────────────────────────────────────
                   What the bot carries from one conversation to the next. Off
                   by default; the field list is the brand's — a clinic ticks
                   name and open references and never diagnoses. The extractor
                   refuses IC / bank / medical details whatever is ticked. -->
              <h6 class="mb-1">{{ $t('organization.memoryTitle') }}</h6>
              <p class="small text-muted">{{ $t('organization.memoryHint') }}</p>
              <b-form-checkbox v-model="form.memoryEnabled" switch class="mb-2">{{ $t('organization.memoryEnabled') }}</b-form-checkbox>
              <div v-if="form.memoryEnabled" class="pl-1">
                <b-form-group :label="$t('organization.memoryFields')" class="mb-2">
                  <b-form-checkbox-group v-model="form.memoryFields" class="memory-fields">
                    <b-form-checkbox v-for="f in memoryKnownFields" :key="f" :value="f">{{ $t('organization.memoryField_' + f) }}</b-form-checkbox>
                  </b-form-checkbox-group>
                  <small class="form-text text-muted">{{ $t('organization.memoryFieldsHint') }}</small>
                </b-form-group>
                <b-form-group :label="$t('organization.memoryCustom')" class="mb-2">
                  <b-form-input v-model="form.memoryCustom" placeholder="budget_range, preferred_trainer" />
                  <small class="form-text text-muted">{{ $t('organization.memoryCustomHint') }}</small>
                </b-form-group>
                <small class="form-text text-muted"><i class="mdi mdi-shield-check-outline mr-1"></i>{{ $t('organization.memoryNever') }}</small>
              </div>


              <hr />

              <!-- ── Tone ──────────────────────────────────────────────────
                   Praise, scolding, venting about a loss, fragments too short
                   to read. Off by default; shadow records the verdict for the
                   desk to check before live changes a single reply. The lines
                   it says are in "What the bot says" (tone.* and handoff.ask_why). -->
              <!-- Industry first: it is a fact about the brand, and it decides
                   which trade's words the bot uses in the lines below. -->
              <b-form-group :label="$t('organization.vertical')" class="mb-3">
                <MultiSelect
                  v-model="form.vertical"
                  :options="verticalDdl"
                  :multiple="false"
                  :allow-empty="true"
                  :placeholder="$t('organization.verticalNone')"
                />
                <small class="form-text text-muted">{{ $t('organization.verticalHint') }}</small>
                <!-- The quiet failure this warning exists for: tone runs
                     either way, and with no industry it reads a gaming
                     player's "makan besar" — he just lost a lot — as a boast
                     and thanks him for it. Nothing else on screen would say
                     why, because nothing is broken. -->
                <div v-if="form.toneMode !== 'off' && !(form.vertical || '').trim()" class="alert alert-warning py-2 px-2 mt-2 mb-0 small">
                  <i class="mdi mdi-alert-outline mr-1"></i>{{ $t('organization.verticalMissing') }}
                </div>
              </b-form-group>

              <h6 class="mb-1">{{ $t('organization.toneTitle') }}</h6>
              <p class="small text-muted">{{ $t('organization.toneSectionHint') }}</p>
              <b-form-group :label="$t('organization.toneMode')" class="mb-2">
                <MultiSelect v-model="form.toneMode" :options="toneModeDdl" :multiple="false" :allow-empty="false" />
                <small v-if="form.toneMode === 'shadow'" class="form-text text-muted">{{ $t('organization.toneShadowNote') }}</small>
              </b-form-group>
              <div v-if="form.toneMode !== 'off'" class="pl-1">
                <b-form-checkbox v-model="form.toneAskWhy" switch class="mb-1">{{ $t('organization.toneAskWhy') }}</b-form-checkbox>
                <small class="form-text text-muted mb-2">{{ $t('organization.toneAskWhyHint') }}</small>
                <b-form-checkbox v-model="form.toneRepeatHandoff" switch class="mb-1">{{ $t('organization.toneRepeat') }}</b-form-checkbox>
                <small class="form-text text-muted mb-2">{{ $t('organization.toneRepeatHint') }}</small>
                <small class="form-text text-muted"><i class="mdi mdi-information-outline mr-1"></i>{{ $t('organization.toneNoHandoff') }}</small>
              </div>

              <hr />

              <!-- ── Handover colours ───────────────────────────────────────
                   Console-only: how a waiting row looks in the inbox list per
                   reason. Saved here, never sent to ai_bot. A reason without a
                   pick keeps the built-in look (deposit teal, overdue red). -->
              <h6 class="mb-1">{{ $t('organization.reasonColorsTitle') }}</h6>
              <p class="small text-muted">{{ $t('organization.reasonColorsHint') }}</p>
              <div class="reason-grid mb-2">
                <div v-for="r in reasonRows()" :key="r" class="reason-row">
                  <span class="reason-swatch" :style="{ background: reasonColors[r] || '#dfe3ea' }"></span>
                  <span class="reason-name">{{ reasonLabel(r) }} <code class="small text-muted">{{ r }}</code></span>
                  <input type="color" class="reason-pick" :value="reasonColors[r] || '#888888'" @input="setReasonColor(r, $event.target.value)" />
                  <b-button v-if="reasonColors[r]" size="sm" variant="link" class="p-0 small" @click="clearReasonColor(r)">{{ $t('organization.reasonColorReset') }}</b-button>
                  <span v-else class="small text-muted">{{ $t('organization.reasonColorDefault') }}</span>
                </div>
              </div>
              <h6 class="mb-1 mt-3">{{ $t('organization.proposalTabTitle') }}</h6>
              <p class="small text-muted mb-1">{{ $t('organization.proposalTabHint') }}</p>
              <b-form-checkbox v-model="proposalTab" switch class="mb-2">{{ $t('organization.proposalTabLabel') }}</b-form-checkbox>
              <div class="text-right mb-2">
                <b-button size="sm" variant="outline-primary" :disabled="reasonColorsSaving" @click="saveReasonColors">
                  {{ reasonColorsSaving ? $t('label.loading') : $t('organization.reasonColorsSave') }}
                </b-button>
              </div>

              <hr />

              <!--
                ── Booking form ────────────────────────────────────────────────
                The questions the bot asks when someone books. Without a form
                every booking attempt dead-ends on "what would you like to
                do?". Four rows are locked: the appointment skill reads them
                to file the request and book the Cal.com slot. Cal.com itself
                (event type, calendar) is operator setup and lives in ai_bot's
                admin page, not here.
              -->
              <!-- Only for a brand whose bot can book: the form is the appointment
                   skill's questions, and without the skill nothing reads it. -->
              <template v-if="form.skills.includes('jk')">
              <h6 class="mb-1">{{ $t('organization.jkNotifyTitle') }}</h6>
              <p class="small text-muted">{{ $t('organization.jkNotifyHint') }}</p>

              <b-form-checkbox v-model="form.jkNotifyDeposit" switch class="mb-1">
                {{ $t('organization.jkNotifyDeposit') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="form.jkNotifyWithdraw" switch class="mb-1">
                {{ $t('organization.jkNotifyWithdraw') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="form.jkNotifyBonusClaimed" switch class="mb-1">
                {{ $t('organization.jkNotifyBonusClaimed') }}
              </b-form-checkbox>
              <small class="form-text text-muted mb-3">{{ $t('organization.jkNotifyBonusHint') }}</small>
              </template>

              <template v-if="form.skills.includes('appointment')">
              <h6 class="mb-1">{{ $t('organization.apptFormTitle') }}</h6>
              <p class="small text-muted">{{ $t('organization.apptFormHint') }}</p>

              <div v-if="!(apptForm && apptForm.ready)" class="small mb-3">
                <span class="badge badge-warning mr-2">{{ $t('organization.apptFormMissing') }}</span>
                <b-button size="sm" variant="outline-primary" :disabled="apptFormCreating" @click="createApptForm">
                  {{ apptFormCreating ? $t('label.loading') : $t('organization.apptFormCreate') }}
                </b-button>
              </div>

              <div v-else class="table-responsive mb-2">
                <table class="table table-sm align-middle mb-2">
                  <thead>
                    <tr class="small text-muted">
                      <th style="width:11rem">{{ $t('organization.apptFieldKey') }}</th>
                      <th>{{ $t('organization.apptFieldLabel') }}</th>
                      <th>{{ $t('organization.apptFieldHint') }}</th>
                      <th style="width:9rem">{{ $t('organization.apptFieldType') }}</th>
                      <th style="width:5rem" class="text-center">{{ $t('organization.apptFieldRequired') }}</th>
                      <th style="width:7rem"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(r, i) in apptRows">
                      <tr :key="'row-' + i" :class="{ 'table-danger': apptRowErrors[String(i)] }">
                        <td>
                          <b-form-input v-model="r.name" size="sm" :disabled="r.locked" placeholder="field_key" />
                          <small v-if="r.locked" class="text-muted">{{ $t('organization.apptFieldLocked') }}</small>
                        </td>
                        <td><b-form-input v-model="r.label" size="sm" :placeholder="$t('organization.apptFieldLabel')" /></td>
                        <td><b-form-input v-model="r.hint" size="sm" :placeholder="$t('organization.apptFieldHintPlaceholder')" /></td>
                        <td>
                          <b-form-select v-model="r.type" size="sm" :disabled="r.name === 'phone'">
                            <b-form-select-option v-for="t in apptTypes" :key="t" :value="t">{{ $t('organization.apptType_' + t) }}</b-form-select-option>
                          </b-form-select>
                          <b-form-input v-if="r.type === 'enum'" v-model="r.options" size="sm" class="mt-1" :placeholder="$t('organization.apptFieldOptions')" />
                        </td>
                        <td class="text-center">
                          <small v-if="r.locked" class="text-muted">{{ $t('organization.apptFieldAlways') }}</small>
                          <b-form-checkbox v-else v-model="r.required" />
                        </td>
                        <td class="text-right text-nowrap">
                          <b-button size="sm" variant="light" :disabled="i === 0" @click="apptMoveRow(i, -1)"><i class="mdi mdi-chevron-up"></i></b-button>
                          <b-button size="sm" variant="light" :disabled="i === apptRows.length - 1" @click="apptMoveRow(i, 1)"><i class="mdi mdi-chevron-down"></i></b-button>
                          <b-button size="sm" variant="light" :disabled="r.locked" :title="$t('organization.apptFieldRemove')" @click="apptRemoveRow(i)"><i class="mdi mdi-close"></i></b-button>
                        </td>
                      </tr>
                      <tr v-if="apptRowErrors[String(i)]" :key="'err-' + i">
                        <td colspan="6" class="small text-danger py-0">{{ apptRowErrors[String(i)] }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
                <small v-if="apptRowErrors._" class="text-danger d-block mb-2">{{ apptRowErrors._ }}</small>
                <div class="d-flex justify-content-between align-items-center">
                  <b-button size="sm" variant="outline-secondary" @click="apptAddRow">
                    <i class="mdi mdi-plus mr-1"></i>{{ $t('organization.apptFieldAdd') }}
                  </b-button>
                  <b-button size="sm" variant="primary" :disabled="apptFormSaving" @click="saveApptForm">
                    {{ apptFormSaving ? $t('label.loading') : $t('organization.apptFormSave') }}
                  </b-button>
                </div>
                <small class="form-text text-muted">{{ $t('organization.apptFormLabelsNote') }}</small>
              </div>
              </template>


              <!--
                Its own Submit.

                This card lost one when two controls were taken out of it, and
                a form you can edit but not save is worse than one you cannot
                edit: the changes look taken until somebody reloads the page.
              -->
                  <div class="text-right">
                <b-button variant="primary" :disabled="saving" @click="saveSettings">
                  {{ saving ? $t('label.loading') : $t('label.submit') }}
                </b-button>
              </div>
            </div>
            </div>
          </b-tab>

          <b-tab :title="$t('organization.tabCopy')">
            <!--
            ── What the bot says ────────────────────────────────────────────
            Four strings, four languages. They lived in an internal API with no
            screen, which is why "答不出来就说 senior manager" was a config change
            on a server rather than something a brand could write themselves.

            Writing twelve more fields by hand is the reason this screen did not
            exist, so one button fills the rest from whichever language is typed —
            and the result is editable and SAVED as fixed strings, so a player
            never waits on a translation and the wording cannot drift turn to turn.
            -->
            <div class="card">
            <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('organization.copyTitle') }}</h6></div>
            <div class="card-body">
              <p class="small text-muted">{{ $t('organization.copyHint') }}</p>
              <div v-if="!copyRows.length" class="small text-muted">{{ $t('organization.copyUnavailable') }}</div>
              <template v-else>
                <div v-for="row in copyRows" :key="row.key" class="copy-block">
                  <div class="small mb-1">
                    <strong>{{ $t('organization.copy_' + row.key.replace('.', '_')) }}</strong>
                  </div>
                  <b-form-input v-model="row.text" size="sm" :placeholder="row.platformText" />
                  <small class="form-text text-muted">{{ $t('organization.copyBlankNote') }}</small>
                </div>
                    <div class="text-right">
                  <b-button variant="primary" :disabled="copySaving" @click="persistCopy">
                    {{ copySaving ? $t('label.loading') : $t('label.submit') }}
                  </b-button>
                </div>
              </template>
            </div>
            </div>
          </b-tab>

          <b-tab :title="$t('organization.tabBackend')">
            <!-- ── Brand backend ────────────────────────────────────────────── -->
            <!-- ── Brand backends ─────────────────────────────────────────
                 One card per provider ai_bot knows how to talk to. The list,
                 the labels and which skill each one serves come from ai_bot's
                 registry (GET credential-providers), so a new provider there
                 is a new card here with no console change. -->
            <p class="small text-muted mb-3">{{ $t('organization.backendHint', [brand.name]) }}</p>
            <div v-if="!creds.length" class="text-muted small">{{ $t('organization.backendUnavailable') }}</div>
            <div v-for="row in visibleCreds()" :key="row.provider" class="card">
              <div class="card-header bg-white py-2 d-flex align-items-center justify-content-between">
                <h6 class="mb-0">
                  {{ row.label }}
                  <code class="ml-2 small text-muted">{{ row.provider }}</code>
                </h6>
                <div class="small">
                  <b-badge :variant="row.endpoint && row.has_secret ? 'success' : 'light'" class="mr-1">
                    {{ row.endpoint && row.has_secret ? $t('organization.backendConfigured') : $t('organization.backendNotConfigured') }}
                  </b-badge>
                  <b-badge v-if="row.skill && row.skill_enabled === false" variant="light" class="text-muted">{{ $t('organization.backendSkillOff', [row.skill]) }}</b-badge>
                  <b-badge v-if="row.last_error" variant="danger" :title="row.last_error">{{ $t('organization.backendLastError') }}</b-badge>
                  <span v-else-if="row.last_ok_at" class="text-muted ml-1">{{ $t('organization.backendLastOk', [String(row.last_ok_at).replace('T', ' ').slice(0, 16)]) }}</span>
                </div>
              </div>
              <div class="card-body">
                <p class="small text-muted">{{ row.hint }}</p>
                <b-row>
                  <b-col cols="12" lg="5">
                    <b-form-group :label="row.fields.endpoint">
                      <b-form-input v-model.trim="row.endpoint" placeholder="https://api.example.com/v1" :state="row.error ? false : null" />
                      <b-form-invalid-feedback>{{ row.error }}</b-form-invalid-feedback>
                      <small class="form-text text-muted">{{ $t('organization.jkEndpointHint') }}</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="3">
                    <b-form-group :label="row.fields.public_id">
                      <b-form-input v-model.trim="row.public_id" />
                      <small class="form-text text-muted">{{ $t('organization.jkPublicIdHint') }}</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="4">
                    <b-form-group :label="row.fields.secret">
                      <!-- No placeholder: a placeholder is never masked, and a
                           token fingerprint shown in plain text read as a leak. -->
                      <b-form-input v-model.trim="row.secret" type="password" autocomplete="new-password" />
                      <small class="form-text text-muted">
                        {{ row.has_secret ? $t('organization.jkSecretKeep', [row.secret_hint]) : $t('organization.jkSecretHint') }}
                      </small>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="text-right">
                  <b-button variant="primary" size="sm" :disabled="row.saving" @click="saveCred(row)">
                    {{ row.saving ? $t('label.loading') : $t('label.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </b-tab>

        </b-tabs>
      </b-card>
    </template>
  </Layout>
</template>

<style scoped>
.reason-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(21rem, 1fr)); gap: 6px 18px; }
.reason-row { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.reason-swatch { width: 14px; height: 14px; border-radius: 3px; flex: 0 0 auto; border: 1px solid rgba(0,0,0,.08); }
.reason-name { flex: 1 1 auto; min-width: 0; }
.reason-pick { width: 34px; height: 24px; padding: 0; border: 1px solid #dfe3ea; border-radius: 4px; background: transparent; cursor: pointer; }

.memory-fields {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: 2px 12px;
}

.lang-tag {
  display: inline-block;
  width: 3.2rem;
  flex: 0 0 auto;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #74788d;
}

.copy-block {
  padding: 0.75rem 0;
  border-bottom: 1px solid #eff2f7;
}
.copy-block:last-of-type {
  border-bottom: 0;
}


.embed-code {
  background: #f7f8fb;
  border: 1px solid #eef0f4;
  border-radius: 6px;
  padding: 12px;
  font-size: 12.5px;
  white-space: pre-wrap;
  word-break: break-all;
  margin-bottom: 12px;
}
</style>
